import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

export function useSignoffs(orgId) {
  const [unitSignoffs, setUnitSignoffs] = useState([]);
  const [workplaceSignoffs, setWorkplaceSignoffs] = useState([]);
  const [knowledgeUnits, setKnowledgeUnits] = useState([]);
  const [levelDates, setLevelDates] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    if (!orgId) return;
    const [us, ws, ku, ld] = await Promise.all([
      supabase.from('unit_signoffs').select('*').eq('org_id', orgId),
      supabase.from('workplace_signoffs').select('*').eq('org_id', orgId),
      supabase.from('knowledge_units').select('*').eq('org_id', orgId),
      supabase.from('level_dates').select('*').eq('org_id', orgId),
    ]);
    setUnitSignoffs(us.data || []);
    setWorkplaceSignoffs(ws.data || []);
    setKnowledgeUnits(ku.data || []);
    setLevelDates(ld.data || []);
    setLoading(false);
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    loadAll();
  }, [orgId, loadAll]);

  async function toggleUnitSignoff(apprenticeId, unitCode, value, signedBy) {
    const existing = unitSignoffs.find(s => s.apprentice_id === apprenticeId && s.unit_code === unitCode);
    if (value) {
      if (existing) {
        await supabase.from('unit_signoffs').update({ signed_off_by: signedBy, signed_off_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('unit_signoffs').insert({
          apprentice_id: apprenticeId,
          unit_code: unitCode,
          signed_off_by: signedBy,
          signed_off_at: new Date().toISOString(),
          org_id: orgId,
        });
      }
    } else if (existing) {
      await supabase.from('unit_signoffs').delete().eq('id', existing.id);
    }
    await loadAll();
  }

  async function toggleWorkplaceSignoff(apprenticeId, unitCode, value, signedBy) {
    const existing = workplaceSignoffs.find(s => s.apprentice_id === apprenticeId && s.unit_code === unitCode);
    if (value) {
      if (existing) {
        await supabase.from('workplace_signoffs').update({ assessed_by: signedBy, assessed_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('workplace_signoffs').insert({
          apprentice_id: apprenticeId,
          unit_code: unitCode,
          assessed_by: signedBy,
          assessed_at: new Date().toISOString(),
          org_id: orgId,
        });
      }
    } else if (existing) {
      await supabase.from('workplace_signoffs').delete().eq('id', existing.id);
    }
    await loadAll();
  }

  async function toggleKnowledge(apprenticeId, unitCode, value, signedBy) {
    const existing = knowledgeUnits.find(k => k.apprentice_id === apprenticeId && k.unit_code === unitCode);
    if (value) {
      if (existing) {
        await supabase.from('knowledge_units').update({ completed_by: signedBy, completed_at: new Date().toISOString() }).eq('id', existing.id);
      } else {
        await supabase.from('knowledge_units').insert({
          apprentice_id: apprenticeId,
          unit_code: unitCode,
          completed_by: signedBy,
          completed_at: new Date().toISOString(),
          org_id: orgId,
        });
      }
    } else if (existing) {
      await supabase.from('knowledge_units').delete().eq('id', existing.id);
    }
    await loadAll();
  }

  async function saveLevelDate(apprenticeId, levelYear, field, value) {
    const existing = levelDates.find(d => d.apprentice_id === apprenticeId && d.level_year === levelYear);
    if (existing) {
      await supabase.from('level_dates').update({ [field]: value }).eq('id', existing.id);
    } else {
      await supabase.from('level_dates').insert({
        apprentice_id: apprenticeId,
        level_year: levelYear,
        [field]: value,
        org_id: orgId,
      });
    }
    await loadAll();
  }

  return {
    unitSignoffs,
    workplaceSignoffs,
    knowledgeUnits,
    levelDates,
    loading,
    toggleUnitSignoff,
    toggleWorkplaceSignoff,
    toggleKnowledge,
    saveLevelDate,
    reload: loadAll,
  };
}
